import { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { LocalStorageKeys } from '@constants/enums';
import {
  AUTH_SCENARIOS,
  VALID_SCENARIOS,
  QUERY_PARAMS,
  QUERY_PARAM_VALUES,
  TIMEOUTS,
} from '@constants/auth.constants';
import { ROUTES } from '@configs/routes';
import { parseEmailFromUrlParam } from '@utils/formater';
import authSvc from '../../../services/auth/authSvc';
import {
  resetPasswordSchema,
  migrationPasswordSchema,
  ResetPasswordFormData,
} from '../../../schemas/auth.schemas';
import useAuthErrorHandler from '../../shared/hooks/useAuthErrorHandler';
import { AUTH_ERROR_TYPES } from '../../shared/hooks/useAuthErrorHandler.types';
import {
  FormData,
  ScenarioFieldConfig,
  ScenarioType,
  UsePasswordResetReturn,
} from './usePasswordReset.types';

const usePasswordReset = (): UsePasswordResetReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getErrorMessage } = useAuthErrorHandler(
    AUTH_ERROR_TYPES.RESET_PASSWORD
  );

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getEmailFromUrl = useCallback(() => {
    const emailParam = searchParams.get(QUERY_PARAMS.EMAIL);

    if (emailParam) {
      return parseEmailFromUrlParam(emailParam);
    }

    return localStorage.getItem(LocalStorageKeys.RESET_EMAIL_PASSWORD) || '';
  }, [searchParams]);

  const queryParams = useMemo(
    () => ({
      code: searchParams.get(QUERY_PARAMS.CODE) || '',
      email: getEmailFromUrl(),
      migration:
        searchParams.get(QUERY_PARAMS.MIGRATION) === QUERY_PARAM_VALUES.TRUE,
    }),
    [searchParams, getEmailFromUrl]
  );

  const scenario = useMemo((): ScenarioType => {
    let determinedScenario: ScenarioType;

    if (queryParams.migration) {
      determinedScenario = AUTH_SCENARIOS.MIGRATION;
    } else if (queryParams.code && queryParams.email) {
      determinedScenario = AUTH_SCENARIOS.RESET;
    } else {
      determinedScenario = AUTH_SCENARIOS.CONFIRM;
    }

    if (!VALID_SCENARIOS.includes(determinedScenario)) {
      throw new Error(
        `Invalid scenario: ${determinedScenario}. Valid scenarios: ${VALID_SCENARIOS.join(
          ', '
        )}`
      );
    }

    return determinedScenario;
  }, [queryParams]);

  const FIELD_CONFIGURATIONS: ScenarioFieldConfig = useMemo(
    () => ({
      [AUTH_SCENARIOS.RESET]: {
        showEmail: true,
        showCode: true,
        emailEditable: false,
        codeEditable: false,
        emailHelperDefault: '',
        codeHelperDefault: '',
      },
      [AUTH_SCENARIOS.CONFIRM]: {
        showEmail: true,
        showCode: true,
        emailEditable: true,
        codeEditable: true,
        emailHelperDefault: '',
        codeHelperDefault: '',
      },
      [AUTH_SCENARIOS.MIGRATION]: {
        showEmail: false,
        showCode: false,
        emailEditable: false,
        codeEditable: false,
        emailHelperDefault: '',
        codeHelperDefault: '',
      },
    }),
    []
  );

  const fieldConfig = FIELD_CONFIGURATIONS[scenario];
  const schema =
    scenario === AUTH_SCENARIOS.MIGRATION
      ? migrationPasswordSchema
      : resetPasswordSchema;
  const defaultValues =
    scenario === AUTH_SCENARIOS.MIGRATION
      ? { password: '', confirmPassword: '' }
      : { email: '', code: '', password: '', confirmPassword: '' };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const isFormValid = useMemo(() => {
    if (scenario === AUTH_SCENARIOS.MIGRATION) {
      return isValid;
    }

    const currentEmail = watch('email');
    const currentCode = watch('code');
    const currentPassword = watch('password');
    const currentConfirmPassword = watch('confirmPassword');

    const hasAllFields = Boolean(
      currentEmail?.trim() &&
        currentCode?.trim() &&
        currentPassword?.trim() &&
        currentConfirmPassword?.trim()
    );

    return isValid && hasAllFields;
  }, [isValid, scenario, watch]);

  const getFieldError = useCallback(
    (fieldName: string): string => {
      const fieldError = errors[fieldName as keyof typeof errors];
      if (!fieldError?.message) return '';
      return t(fieldError.message as string);
    },
    [errors, t]
  );

  const scenarioContent = useMemo(() => {
    switch (scenario) {
      case AUTH_SCENARIOS.RESET:
        return {
          title: t('resetScenarioTitle'),
          description: t('resetScenarioDescription'),
        };
      case AUTH_SCENARIOS.CONFIRM:
        return {
          title: t('confirmScenarioTitle'),
          description: t('confirmScenarioDescription'),
        };
      case AUTH_SCENARIOS.MIGRATION:
        return {
          title: t('migrationScenarioTitle'),
          description: t('migrationScenarioDescription'),
        };
      default:
        return {
          title: t('resetPasswordTitle'),
          description: t('resetPasswordDescription'),
        };
    }
  }, [scenario, t]);

  const getSuccessMessage = useCallback(() => {
    switch (scenario) {
      case AUTH_SCENARIOS.RESET:
      case AUTH_SCENARIOS.CONFIRM:
        return t('resetPasswordSuccessMessage');
      case AUTH_SCENARIOS.MIGRATION:
        return t('passwordUpdateSuccessMessage');
      default:
        return t('resetPasswordSuccessMessage');
    }
  }, [scenario, t]);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleClickShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  const handleBackToLogin = useCallback(() => {
    navigate(ROUTES.AUTH.LOGIN);
  }, [navigate]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        setIsLoading(true);
        setError('');
        setSuccess(false);

        if (scenario === AUTH_SCENARIOS.MIGRATION) {
          const idToken = localStorage.getItem(
            LocalStorageKeys.MIGRATION_ID_TOKEN
          );
          const email = localStorage.getItem(
            LocalStorageKeys.RESET_EMAIL_PASSWORD
          );

          if (!idToken) {
            throw new Error('Missing migration token');
          }
          if (!email) {
            throw new Error('Missing email for migration');
          }

          await authSvc.updatePassword(
            {
              email,
              password: data.password,
            },
            idToken
          );

          localStorage.removeItem(LocalStorageKeys.MIGRATION_ID_TOKEN);
        } else {
          const resetData = data as ResetPasswordFormData;

          if (!resetData.email || !resetData.code) {
            throw new Error(t('errorDefault') as string);
          }

          await authSvc.cognitoConfirmPassword({
            email: resetData.email.toLowerCase(),
            code: resetData.code,
            newPassword: resetData.password,
          });
        }

        setSuccess(true);
        localStorage.removeItem(LocalStorageKeys.RESET_EMAIL_PASSWORD);

        const successMessage =
          scenario === AUTH_SCENARIOS.MIGRATION
            ? t('passwordUpdateSuccessMessage')
            : t('resetPasswordSuccessRedirectMessage');

        const email =
          scenario === AUTH_SCENARIOS.MIGRATION
            ? localStorage.getItem(LocalStorageKeys.RESET_EMAIL_PASSWORD) || ''
            : (data as ResetPasswordFormData).email;

        setTimeout(() => {
          navigate(ROUTES.AUTH.LOGIN, {
            state: {
              message: successMessage,
              email,
              isSuccessMessage: true,
            },
          });
        }, TIMEOUTS.REDIRECT_TIMEOUT);
      } catch (err: any) {
        console.error('Password update/confirm error:', err);
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [scenario, t, navigate, getErrorMessage]
  );

  useEffect(() => {
    if (scenario !== AUTH_SCENARIOS.MIGRATION) {
      if (queryParams.email) {
        setValue('email', queryParams.email, { shouldValidate: true });
      }

      if (queryParams.code) {
        setValue('code', queryParams.code, { shouldValidate: true });
      }
    }
  }, [queryParams.email, queryParams.code, setValue, scenario]);

  const currentEmail = watch('email');
  const currentCode = watch('code');

  const isEmailDisabled =
    scenario === AUTH_SCENARIOS.MIGRATION
      ? true
      : Boolean(currentEmail && queryParams.email);
  const isCodeDisabled =
    scenario === AUTH_SCENARIOS.MIGRATION
      ? true
      : Boolean(currentCode && queryParams.code);

  const getEmailHelperText = useCallback(() => {
    if (getFieldError('email')) return getFieldError('email');
    if (isEmailDisabled) return t('resetPasswordEmailAutoFilled');
    return fieldConfig.emailHelperDefault;
  }, [getFieldError, isEmailDisabled, fieldConfig.emailHelperDefault, t]);

  const getCodeHelperText = useCallback(() => {
    if (getFieldError('code')) return getFieldError('code');
    if (isCodeDisabled) return t('resetPasswordCodeAutoFilled');

    return fieldConfig.codeHelperDefault || t('resetPasswordCodeHelper');
  }, [getFieldError, isCodeDisabled, fieldConfig.codeHelperDefault, t]);

  return {
    register,
    handleSubmit,
    control,
    setValue,
    errors,
    isValid: isFormValid,
    success,
    error,
    isLoading,
    showPassword,
    showConfirmPassword,
    isEmailDisabled,
    isCodeDisabled,
    scenario,
    handleClickShowPassword,
    handleClickShowConfirmPassword,
    handleBackToLogin,
    onSubmit,
    getFieldError,
    scenarioContent,
    getEmailHelperText,
    getCodeHelperText,
    getSuccessMessage,
    fieldConfig,
  };
};

export default usePasswordReset;
